'use client';

import { useEffect, useState } from 'react';

interface BottomModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    maxWidth?: string;
}

export default function BottomModal({ isOpen, onClose, children, title, maxWidth = '50vw' }: BottomModalProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        } else {
            setIsVisible(false);
            const timer = setTimeout(() => {
                document.body.style.overflow = 'unset';
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!isOpen && !isVisible) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
                pointerEvents: isVisible ? 'auto' : 'none'
            }}
        >
            <div
                className="fixed inset-0 bg-black transition-opacity duration-300"
                style={{
                    opacity: isVisible ? 0.5 : 0,
                    transition: 'opacity 300ms ease-in-out',
                    pointerEvents: isVisible ? 'auto' : 'none'
                }}
                onClick={handleOverlayClick}
            />

            <div
                className="relative bg-white rounded-2xl shadow-lg max-h-[90vh] overflow-hidden flex flex-col w-full"
                style={{
                    maxWidth: maxWidth,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                    transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out',
                    pointerEvents: isVisible ? 'auto' : 'none'
                }}
            >
                {title && (
                    <div className="flex-shrink-0 bg-white z-20 px-6 pt-6 pb-6 border-b border-gray-100">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 cursor-pointer hover:opacity-70 transition-opacity z-10"
                            aria-label="Закрыть"
                        >
                            <svg 
                                width="40" 
                                height="40" 
                                viewBox="0 0 40 40" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-6 h-6 lg:w-10 lg:h-10"
                            >
                                <path 
                                    d="M10 10L30 30M30 10L10 30" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round"
                                    className="text-gray-400"
                                />
                            </svg>
                        </button>
                        <h2 className="text-[18px] lg:text-[20px] font-bold text-black leading-[1.2] uppercase pr-10">
                            {title}
                        </h2>
                    </div>
                )}
                
                <div className="overflow-y-auto flex-1">
                    {children}
                </div>
            </div>
        </div>
    );
}